import type {
  AttributionRow,
  ConsentSettings,
  MarketingSettingsView,
  PaymentMode,
  SearchConsoleStatus,
  SearchPerformance,
  TagConfigView,
  WhatsAppSettings,
} from '@dunea/shared';
import httpClient from '~/lib/http-client';

// Owner-facing Marketing + Search Console plugin APIs (studio
// `MarketingOwnerController`, `SearchConsoleOwnerController`). `appId` is the
// CHAT id; settings apply to the published site on its next publish.

export type { AttributionRow, ConsentSettings, MarketingSettingsView, SearchConsoleStatus, SearchPerformance };
export type { TagConfigView, WhatsAppSettings };

export interface TagInput {
  enabled: boolean;
  /** Public ids only. */
  values: Record<string, string>;
  /** Write-only. Omit to keep stored secrets; a null value clears one. */
  secrets?: Record<string, string | null>;
}

/** PUT whatsapp: full settings, or `{ enabled: false }` to turn the button off and keep the rest. */
export type WhatsAppInput = WhatsAppSettings | { enabled: false };

const base = (appId: string) => `/api/plugins/marketing/apps/${encodeURIComponent(appId)}`;
const scBase = (appId: string) => `/api/plugins/search-console/apps/${encodeURIComponent(appId)}`;

export async function apiGetMarketing(appId: string): Promise<MarketingSettingsView> {
  const { data } = await httpClient.get<MarketingSettingsView>(base(appId));
  return data;
}

export async function apiSaveMarketingTag(appId: string, provider: string, input: TagInput): Promise<TagConfigView> {
  const { data } = await httpClient.put<TagConfigView>(`${base(appId)}/tags/${encodeURIComponent(provider)}`, input);
  return data;
}

export async function apiSaveConsent(appId: string, consent: ConsentSettings): Promise<ConsentSettings> {
  const { data } = await httpClient.put<ConsentSettings>(`${base(appId)}/consent`, consent);
  return data;
}

export async function apiSaveWhatsApp(appId: string, input: WhatsAppInput): Promise<WhatsAppSettings | null> {
  const { data } = await httpClient.put<WhatsAppSettings | null>(`${base(appId)}/whatsapp`, input);
  return data;
}

export async function apiSetServerSideConversions(appId: string, enabled: boolean): Promise<{ enabled: boolean }> {
  const { data } = await httpClient.put<{ enabled: boolean }>(`${base(appId)}/server-side`, { enabled });
  return data;
}

export async function apiGetAttribution(appId: string, days: number, mode: PaymentMode): Promise<AttributionRow[]> {
  const { data } = await httpClient.get<AttributionRow[]>(`${base(appId)}/attribution?days=${days}&mode=${mode}`);
  return data;
}

// ─── Search Console ───

export async function apiGetSearchConsole(appId: string): Promise<SearchConsoleStatus> {
  const { data } = await httpClient.get<SearchConsoleStatus>(scBase(appId));
  return data;
}

/** Starts Google OAuth. `returnUrl` must be a Dunea URL; Google sends the owner back with ?searchConsole=connected|error. */
export async function apiConnectSearchConsole(appId: string, returnUrl: string): Promise<{ url: string }> {
  const { data } = await httpClient.post<{ url: string }>(`${scBase(appId)}/connect`, { returnUrl });
  return data;
}

export async function apiVerifySearchConsole(appId: string): Promise<SearchConsoleStatus> {
  const { data } = await httpClient.post<SearchConsoleStatus>(`${scBase(appId)}/verify`, {});
  return data;
}

export async function apiSubmitSitemap(appId: string): Promise<SearchConsoleStatus> {
  const { data } = await httpClient.post<SearchConsoleStatus>(`${scBase(appId)}/sitemap`, {});
  return data;
}

export async function apiGetSearchPerformance(appId: string, days = 28): Promise<SearchPerformance> {
  const { data } = await httpClient.get<SearchPerformance>(`${scBase(appId)}/performance?days=${days}`);
  return data;
}

export async function apiDisconnectSearchConsole(appId: string): Promise<SearchConsoleStatus> {
  const { data } = await httpClient.post<SearchConsoleStatus>(`${scBase(appId)}/disconnect`, {});
  return data;
}
